import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { GradientCard } from '../../components/ListPageGradientSummary';
import { displayError } from '../../helpers';
import '../../button.css';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllProducts } from '../../services/ProductServices';
import { getAllEquipments } from '../../services/EquipmentServices';
import TableRowsDieselConsumption from './TableRowsDieselConsumption';
import {
    cancelDieselConsumption,
    deleteDieselConsumption,
    getDieselConsumptionDetails,
    getDieselDriverOptions,
    updateDieselConsumption,
} from '../../services/DieselConsumptionServices';
import { getStockStatement } from '../../services/ConsumptionNoteServices';
import { loadStoredDrivers, persistDriverName, toDriverOptions } from './dieselDriverUtils';
import { buildDieselStockSummaryCards } from './dieselStockUtils';

function displayDateToInput(value) {
    if (!value) return '';
    const parts = String(value).split('-');
    if (parts.length === 3 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return value;
}

function DieselConsumptionEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [equipments, setEquipments] = useState([]);
    const [products, setProducts] = useState([]);
    const [driverOptions, setDriverOptions] = useState([]);
    const [stockByProduct, setStockByProduct] = useState({});
    const [stockAdjustByProduct, setStockAdjustByProduct] = useState({});
    const [rowsData, setRowsData] = useState([]);
    const [inProgress, setInProgress] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [inputs, setInputs] = useState({});

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getAllEquipments(cookies).then((response) => {
            const list = response.equipment_list || response.equipment_data || response.equipments || [];
            setEquipments(list.filter((e) => e.status === true || e.status === 1 || e.status === 'Active'));
        });
        getAllProducts(cookies).then((response) => {
            const list = response.product_list || response.product_data || response.products || [];
            setProducts(list.filter((p) => p.status));
        });
        getDieselDriverOptions(cookies)
            .then((res) => {
                setDriverOptions(toDriverOptions([...(res.drivers || []), ...loadStoredDrivers()]));
            })
            .catch(() => {
                setDriverOptions(toDriverOptions(loadStoredDrivers()));
            });
    }, [cookies]);

    const dieselProducts = useMemo(
        () => products.filter((p) => {
            const name = String(p.name || '').toLowerCase();
            const unit = String(p.unit?.name || p.unit_name || '').toLowerCase();
            return name.includes('diesel') || unit.includes('liter') || unit.includes('litre') || unit.includes('kiloliter');
        }),
        [products],
    );

    useEffect(() => {
        if (!id) return;
        setInProgress(true);
        getDieselConsumptionDetails(cookies, id)
            .then((record) => {
                const active = record.status === true || record.status === 1 || record.status === 'Active';
                setIsActive(active);
                if (!active) {
                    navigate(`/DieselConsumptionView/${id}`, { replace: true });
                    return;
                }
                setInputs({
                    plant_id: record.plant_id,
                    consumption_date: displayDateToInput(record.consumption_date),
                    consumption_time: record.consumption_time?.slice(0, 5) || '',
                    consumption_no: record.sl_no,
                    prefix: record.prefix,
                    issued_by: record.issued_by || '',
                    remarks: record.remarks || '',
                });
                setRowsData(record.detail_list || []);
                const adjust = {};
                (record.detail_list || []).forEach((row) => {
                    if (!row.product_id) return;
                    const key = String(row.product_id);
                    adjust[key] = Number(((adjust[key] || 0) + (Number(row.consumed_qty) || 0)).toFixed(3));
                });
                setStockAdjustByProduct(adjust);
                const lineDrivers = (record.detail_list || []).map((row) => row.driver_name).filter(Boolean);
                if (lineDrivers.length) {
                    setDriverOptions((prev) => toDriverOptions([
                        ...prev.map((o) => o.value),
                        ...lineDrivers,
                        ...loadStoredDrivers(),
                    ]));
                }
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id, navigate]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.consumption_date) {
            setStockByProduct({});
            return;
        }
        const asOnDate = moment(inputs.consumption_date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        getStockStatement(cookies, Number(inputs.plant_id), asOnDate, 'cumulative')
            .then((res) => {
                const map = {};
                (res.stock_statement || []).forEach((row) => {
                    if (row.product_id != null) {
                        map[String(row.product_id)] = row;
                    }
                });
                setStockByProduct(map);
            })
            .catch(() => setStockByProduct({}));
    }, [cookies, inputs.plant_id, inputs.consumption_date]);

    const totalQty = useMemo(
        () => rowsData.reduce((sum, row) => sum + (Number(row.consumed_qty) || 0), 0),
        [rowsData],
    );

    const summaryCards = useMemo(
        () => buildDieselStockSummaryCards(stockByProduct, {
            dieselProductIds: dieselProducts.map((p) => p.id),
        }),
        [stockByProduct, dieselProducts],
    );

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const payload = {
            plant_id: Number(inputs.plant_id),
            consumption_date: moment(inputs.consumption_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
            consumption_time: inputs.consumption_time,
            consumption_no: inputs.consumption_no,
            prefix: inputs.prefix,
            issued_by: inputs.issued_by,
            remarks: inputs.remarks,
            detail_list: rowsData.map((row, index) => ({
                line_no: index + 1,
                equipment_id: row.equipment_id || null,
                driver_name: row.driver_name,
                purpose: row.purpose,
                product_id: row.product_id,
                opening_meter: Number(row.opening_meter) || 0,
                closing_meter: Number(row.closing_meter) || 0,
                consumed_qty: Number(row.consumed_qty) || 0,
                unit: row.unit || 'Ltr',
            })),
        };

        setInProgress(true);
        try {
            await updateDieselConsumption(cookies, id, payload);
            Swal.fire('Updated', 'Diesel consumption updated successfully.', 'success');
        } catch (error) {
            displayError(error?.response?.data || error, 'Update Failed');
        } finally {
            setInProgress(false);
        }
    };

    const handleCancelVoucher = () => {
        Swal.fire({
            title: 'Cancel diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setInProgress(true);
            cancelDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Cancelled', 'Diesel consumption cancelled.', 'success');
                    navigate(`/DieselConsumptionView/${id}`);
                })
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setInProgress(true);
            deleteDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Record deleted.', 'success');
                    navigate('/DieselConsumptionTable');
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleAddLine = () => {
        setRowsData((prev) => [...prev, {
            line_no: prev.length + 1,
            equipment_id: '',
            equipment_name: '',
            driver_name: '',
            purpose: '',
            product_id: dieselProducts[0]?.id || '',
            product_name: dieselProducts[0]?.name || '',
            opening_meter: 0,
            closing_meter: 0,
            consumed_qty: 0,
            unit: dieselProducts[0]?.unit?.name || 'Ltr',
        }]);
    };

    const handleCreateDriver = (name) => {
        persistDriverName(name);
        setDriverOptions((prev) => toDriverOptions([...(prev.map((o) => o.value)), name]));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls
                        tableLink="/DieselConsumptionTable"
                        enableCancel
                        onCancel={() => navigate('/DieselConsumptionTable')}
                    />

                    <FormPageLayout
                        moduleLabel="STORE"
                        title="Diesel Consumption"
                        subtitle="Update equipment lines, meter readings, and issued details for this diesel consumption voucher."
                        modeLabel="UPDATE MODE"
                        headerActions={summaryCards.map((card) => (
                            <GradientCard key={card.label} card={card} />
                        ))}
                    >
                    <div className="container form-control-panel">
                        <Row className="form-equal-height-row">
                            <Col xl={7} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel form-panel-fill">
                                    <h4 className="col-sm-11"><b>Voucher Details</b></h4><br /><br />

                                    <label htmlFor="plant_id" className="form-group col-sm-4 text-right">Plant</label>
                                    <select
                                        id="plant_id"
                                        name="plant_id"
                                        className="form-control col-sm-7 mandatory-form-control"
                                        value={inputs.plant_id || ''}
                                        onChange={handleChange}
                                        required
                                        disabled
                                    >
                                        <option value="">Select Plant</option>
                                        {plants.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select><br />

                                    <label htmlFor="issued_by" className="form-group col-sm-4 text-right">Issued By</label>
                                    <input
                                        id="issued_by"
                                        name="issued_by"
                                        className="form-control col-sm-7"
                                        value={inputs.issued_by || ''}
                                        onChange={handleChange}
                                    /><br />

                                    <label htmlFor="remarks" className="form-group col-sm-4 text-right">Remarks</label>
                                    <textarea
                                        id="remarks"
                                        name="remarks"
                                        className="form-control col-sm-7"
                                        rows="2"
                                        value={inputs.remarks || ''}
                                        onChange={handleChange}
                                    /><br />
                                </div>
                            </Col>
                            <Col xl={5} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel form-panel-fill">
                                    <h4 className="col-sm-11"><b>Document Details</b></h4><br /><br />

                                    <label className="form-group col-sm-4 text-right">Consumption No</label>
                                    <input
                                        className="form-control col-sm-7"
                                        value={`${inputs.prefix || ''}/${inputs.consumption_no || ''}`}
                                        readOnly
                                    /><br />

                                    <label htmlFor="consumption_date" className="form-group col-sm-4 text-right">Date</label>
                                    <input
                                        type="date"
                                        id="consumption_date"
                                        name="consumption_date"
                                        className="form-control col-sm-7"
                                        value={inputs.consumption_date || ''}
                                        onChange={handleChange}
                                        required
                                    /><br />

                                    <label htmlFor="consumption_time" className="form-group col-sm-4 text-right">Time</label>
                                    <input
                                        type="time"
                                        id="consumption_time"
                                        name="consumption_time"
                                        className="form-control col-sm-7"
                                        value={inputs.consumption_time || ''}
                                        onChange={handleChange}
                                    /><br />
                                </div>
                            </Col>
                        </Row>

                        <div className="table-responsive shadow p-2 my-2 border-secondary">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h4 className="mb-0"><b>Diesel Consumption</b></h4>
                                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                                    <label className="mb-0 font-weight-bold">Total Diesel Qty</label>
                                    <input
                                        className="form-control form-control-sm"
                                        value={totalQty.toFixed(3)}
                                        readOnly
                                        style={{ width: '110px', textAlign: 'right', fontWeight: 'bold' }}
                                    />
                                </div>
                            </div>
                            <table className="table table-bordered table-sm">
                                <thead className="thead-light text-center">
                                    <tr>
                                        <th style={{ width: 70 }}>Action</th>
                                        <th>#</th>
                                        <th>Purpose</th>
                                        <th>Equipment</th>
                                        <th>Driver</th>
                                        <th>Diesel Product</th>
                                        <th>Opening Meter</th>
                                        <th>Closing Meter</th>
                                        <th>Consumed Qty</th>
                                        <th>Unit</th>
                                        <th>K(HM) Reading</th>
                                        <th>Avg K(H)M/ Ltr.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <TableRowsDieselConsumption
                                        rowsData={rowsData}
                                        equipments={equipments}
                                        dieselProducts={dieselProducts}
                                        driverOptions={driverOptions}
                                        stockByProduct={stockByProduct}
                                        stockAdjustByProduct={stockAdjustByProduct}
                                        plantSelected={Boolean(inputs.plant_id)}
                                        onChange={setRowsData}
                                        onAdd={handleAddLine}
                                        onRemove={(index) => setRowsData((prev) => prev.filter((_, i) => i !== index))}
                                        onCreateDriver={handleCreateDriver}
                                    />
                                </tbody>
                            </table>
                        </div>

                        <div className="footer text-center">
                            <Button type="submit" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }}>
                                Update
                            </Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={handleCancelVoucher}>
                                Cancel
                            </Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/DieselConsumptionTable')}>
                                View
                            </Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}>
                                Home
                            </Button>
                        </div>
                    </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default DieselConsumptionEdit;
